import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Plus, Pencil, Trash2, RefreshCw, X, Check, AlertCircle } from "lucide-react";

const PROVIDERS = [
  { v: "booking", label: "Booking.com", color: "#003580" },
  { v: "airbnb",  label: "Airbnb",      color: "#FF5A5F" },
  { v: "expedia", label: "Expedia",     color: "#F7B801" },
  { v: "vrbo",    label: "Vrbo",        color: "#2E86DE" },
  { v: "other",   label: "Altro",       color: "#666666" },
];

const providerMeta = (v) => PROVIDERS.find(p => p.v === v) || PROVIDERS[PROVIDERS.length - 1];

/**
 * Per-room iCal sync manager. Loads /api/ical/status and filters to the current
 * room. Supports Add / Edit / Retry / Delete. Mounted inside every room card of
 * the RoomsTab so hosts don't have to leave the room they're editing.
 */
export default function IcalSyncSection({ roomId }) {
  const { lang } = useLang();
  const IT = lang === "it";

  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null); // `${source}:${op}` for spinner state
  const [dlg, setDlg] = useState(null); // null | { mode: "add" | "edit", source, url }

  const load = () => api.get("/ical/status").then(r => {
    const all = r.data.feeds || [];
    setFeeds(all.filter(f => f.room_id === roomId));
  }).catch(() => setFeeds([])).finally(() => setLoading(false));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [roomId]);

  const openAdd = () => {
    const used = new Set(feeds.map(f => f.source));
    const nextSource = PROVIDERS.find(p => !used.has(p.v))?.v || "other";
    setDlg({ mode: "add", source: nextSource, url: "" });
  };
  const openEdit = (f) => setDlg({ mode: "edit", source: f.source, url: f.url, originalSource: f.source });

  const save = async () => {
    if (!dlg?.url || !/^https?:\/\//i.test(dlg.url)) {
      toast.error(IT ? "URL non valido (deve iniziare con https://)" : "Invalid URL (must start with https://)");
      return;
    }
    setBusyKey(`${dlg.source}:save`);
    try {
      // Edit-with-source-change: drop the old feed first so we don't leave duplicates
      if (dlg.mode === "edit" && dlg.originalSource && dlg.originalSource !== dlg.source) {
        try { await api.post("/ical/delete", { room_id: roomId, source: dlg.originalSource }); }
        catch (e) { /* soft-fail, we still try the import */ }
      }
      await api.post("/ical/import", { room_id: roomId, source: dlg.source, ical_url: dlg.url });
      toast.success(IT ? "Feed sincronizzato" : "Feed synced");
      setDlg(null); load();
    } catch (e) { toast.error(errMsg(e, IT ? "Sync fallito" : "Sync failed")); load(); }
    finally { setBusyKey(null); }
  };

  const retry = async (source) => {
    setBusyKey(`${source}:retry`);
    try {
      const r = await api.post("/ical/resync", { room_id: roomId, source });
      toast.success(IT ? `Sync riuscito: ${r.data.imported} eventi` : `Sync ok: ${r.data.imported} events`);
      load();
    } catch (e) { toast.error(errMsg(e, IT ? "Sync fallito" : "Sync failed")); load(); }
    finally { setBusyKey(null); }
  };

  const del = async (source) => {
    if (!window.confirm(IT
      ? `Eliminare il feed ${providerMeta(source).label}? Verranno rimossi anche gli eventi importati.`
      : `Delete the ${providerMeta(source).label} feed? Imported events will also be removed.`)) return;
    setBusyKey(`${source}:del`);
    try {
      await api.post("/ical/delete", { room_id: roomId, source });
      toast.success(IT ? "Feed eliminato" : "Feed deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyKey(null); }
  };

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(IT ? "it-IT" : "en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return String(iso).slice(0, 16); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/70" data-testid={`ical-sync-section-${roomId}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-sage font-semibold">
          <CalendarClock className="w-3 h-3"/>{IT ? "Sincronizzazione calendari" : "Calendar sync"}
        </div>
        <button
          onClick={openAdd}
          className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
          data-testid={`ical-add-${roomId}`}
        >
          <Plus className="w-3 h-3"/>{IT ? "Aggiungi feed" : "Add feed"}
        </button>
      </div>

      {loading ? (
        <div className="text-[11px] text-muted-foreground">…</div>
      ) : feeds.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">
          {IT ? "Nessun feed OTA collegato. Aggiungi Booking, Airbnb, Vrbo..." : "No OTA feed connected. Add Booking, Airbnb, Vrbo..."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {feeds.map((f) => {
            const pm = providerMeta(f.source);
            const isErr = f.ok === false;
            return (
              <li
                key={f.source}
                className="flex items-center gap-2 flex-wrap text-[11px] border border-border/60 bg-muted/20 rounded px-2 py-1.5"
                data-testid={`ical-feed-item-${f.source}`}
              >
                <span
                  className="uppercase text-[9px] px-1.5 py-0.5 rounded text-white font-semibold shrink-0"
                  style={{ background: pm.color }}
                >
                  {pm.label}
                </span>
                <span className={`inline-flex items-center gap-1 shrink-0 ${isErr ? "text-destructive" : "text-sage"}`}>
                  {isErr ? <AlertCircle className="w-3 h-3"/> : <Check className="w-3 h-3"/>}
                  {isErr ? (IT ? "Errore" : "Failing") : "OK"}
                </span>
                <span className="text-muted-foreground shrink-0">{fmtWhen(f.last_attempt)}</span>
                {isErr && f.error && (
                  <span className="basis-full text-destructive text-[10px]" title={f.error}>
                    {f.error.length > 90 ? f.error.slice(0, 90) + "…" : f.error}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => retry(f.source)}
                    disabled={busyKey === `${f.source}:retry`}
                    className="p-1 hover:bg-primary/10 rounded text-primary disabled:opacity-40"
                    title={IT ? "Riprova sync" : "Retry"}
                    data-testid={`ical-retry-${f.source}`}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${busyKey === `${f.source}:retry` ? "animate-spin" : ""}`}/>
                  </button>
                  <button
                    onClick={() => openEdit(f)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground"
                    title={IT ? "Modifica URL" : "Edit URL"}
                    data-testid={`ical-edit-${f.source}`}
                  >
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button
                    onClick={() => del(f.source)}
                    disabled={busyKey === `${f.source}:del`}
                    className="p-1 hover:bg-destructive/10 rounded text-destructive disabled:opacity-40"
                    title={IT ? "Elimina feed" : "Delete feed"}
                    data-testid={`ical-del-${f.source}`}
                  >
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Inline add/edit form */}
      {dlg && (
        <div className="mt-2 border border-primary/30 bg-primary/5 rounded p-3 space-y-2" data-testid={`ical-form-${roomId}`}>
          <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">
            {dlg.mode === "edit"
              ? (IT ? "Modifica feed" : "Edit feed")
              : (IT ? "Nuovo feed" : "New feed")}
          </div>
          <div className="grid sm:grid-cols-[140px_1fr] gap-2">
            <Select value={dlg.source} onValueChange={(v) => setDlg({ ...dlg, source: v })}>
              <SelectTrigger className="h-9" data-testid={`ical-form-source-${roomId}`}>
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              type="url"
              value={dlg.url}
              onChange={(e) => setDlg({ ...dlg, url: e.target.value })}
              placeholder="https://..."
              className="h-9 text-xs font-mono"
              data-testid={`ical-form-url-${roomId}`}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setDlg(null)}
              className="text-xs text-muted-foreground hover:text-foreground px-2"
              data-testid={`ical-form-cancel-${roomId}`}
            >
              <X className="w-3.5 h-3.5 inline mr-0.5"/>{IT ? "Annulla" : "Cancel"}
            </button>
            <Button
              size="sm"
              onClick={save}
              disabled={busyKey?.endsWith(":save")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-8"
              data-testid={`ical-form-save-${roomId}`}
            >
              {busyKey?.endsWith(":save") ? <RefreshCw className="w-3.5 h-3.5 animate-spin"/> : (IT ? "Salva e sincronizza" : "Save & sync")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
